import { useCallback, useEffect, useMemo } from "react";

import AccountBalanceOutlined from "@mui/icons-material/AccountBalanceOutlined";
import ArticleOutlined from "@mui/icons-material/ArticleOutlined";
import CreditScoreOutlined from "@mui/icons-material/CreditScoreOutlined";
import DriveFileMoveOutlined from "@mui/icons-material/DriveFileMoveOutlined";
import FunctionsOutlined from "@mui/icons-material/FunctionsOutlined";
import LogoutOutlined from "@mui/icons-material/LogoutOutlined";

import useMediaQuery from "@mui/material/useMediaQuery";
import { useStytchB2BClient } from "@stytch/react/b2b";
import isNil from "lodash/isNil";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  AddCircleOutline,
  AddRounded,
  DriveFolderUpload,
  DriveFolderUploadOutlined,
  EmailOutlined,
  HistoryOutlined,
  PeopleOutline,
  PersonOutlined,
  RocketLaunchOutlined,
  ScienceOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import DashboardLayout from "../_src_shared/DashboardLayout";
import { useEnv } from "../hooks/useEnv";
import { useFetchedSelectionsRequestsHook } from "../hooks/useFetchedSelectionsRequestsHook";
import { useUsersHook } from "../hooks/useUsersHook";
import { userActionSet } from "../redux/actions/userActions";
import { usersActionSet } from "../redux/actions/usersActions";
import { usePermissionsHook } from "../hooks/usePermissionsHook";

const AuthenticatedDashboard = ({ children }) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const user = useSelector((state) => state.user);
  const users = useSelector((state) => state.users);
  const fileDocGroupAndTypes = useSelector(
    (state) => state.fetchedSelections?.fileDocGroupAndTypes,
  );
  const dispatch = useDispatch();
  const { isAdmin } = usePermissionsHook();
  const { isLendmarq } = useEnv();
  const navSidebarOpen = useSelector((state) => state?.navSidebar?.open);

  const open = useMemo(
    () => navSidebarOpen || isMobile,
    [navSidebarOpen, isMobile],
  );

  const stytch = useStytchB2BClient();
  const navigate = useNavigate();

  const handleLogoutUser = useCallback(async () => {
    await stytch.session.revoke();
    navigate("/");
  }, [stytch, navigate]);

  const topLinks = useMemo(() => {
    const baseItems = [
      { icon: <AddCircleOutline />, to: "/loan/create", label: "Create Loan" },
      // ...(isLendmarq
      //   ? [
      //       {
      //         icon: <ArticleOutlined />,
      //         to: "/loans/all",
      //         label: "Loan Pipeline",
      //         children: [
      //           {
      //             to: "/loans/all",
      //             label: "Full Pipeline",
      //           },
      //           {
      //             to: "/loans/underwriting", // changed from "/loans/all"
      //             label: "Underwriting",
      //           },
      //           {
      //             to: "/loans/scenarios",
      //             label: "Scenarios",
      //           },
      //           {
      //             to: "/loans/borrower-portal-scenarios",
      //             label: "Borrower Portal Scenarios",
      //           },

      //           {
      //             to: "/loans/legacy",
      //             // to: "/loans",
      //             // to: "/pipeline/legacy",
      //             label: "(Legacy) Pipeline Table",
      //           },
      //         ],
      //       },
      //     ]
      //   : [
      //       {
      //         icon: <ArticleOutlined />,
      //         to: "/loans",
      //         label: "Loan Pipeline",
      //       },
      //     ]),
      {
        icon: <ArticleOutlined />,
        to: "/loans/all",
        label: "Loan Pipeline",
        children: [
          {
            to: "/loans/all",
            label: "Full Pipeline",
          },
          {
            to: "/loans/underwriting", // changed from "/loans/all"
            label: "Underwriting",
          },
          {
            to: "/loans/scenarios",
            label: "Scenarios",
          },
          {
            to: "/loans/borrower-portal-scenarios",
            label: "Borrower Portal Scenarios",
          },

          {
            to: "/loans/legacy",
            // to: "/loans",
            // to: "/pipeline/legacy",
            label: "(Legacy) Pipeline Table",
          },
        ],
      },
      {
        icon: <AccountBalanceOutlined />,
        to: "/loan-applications",
        label: "Loan Applications",
      },

      // Conditionally include Quick Applications

      {
        icon: <CreditScoreOutlined />,
        to: "/credit-authorizations",
        label: "Credit Authorizations",
      },
      ...(isLendmarq && isAdmin
        ? [
            {
              icon: <PeopleOutline />,
              to: "/users",
              label: "Borrower Portal Users",
            },
            {
              icon: <SwapHorizOutlined />,
              to: "/orders",
              label: "External Orders",
              children: [
                {
                  to: "/orders/appraisals",
                  label: "Appraisals",
                },
              ],
            },
          ]
        : []),
      ...(isLendmarq
        ? [
            {
              icon: <RocketLaunchOutlined />,
              to: "/quick-applications",
              label: "Quick Applications",
            },
          ]
        : []),
      {
        icon: <FunctionsOutlined className="thin" />,
        to: "/loan/pricing-sizer",
        label: "Loan Intake Pricing Sizer",
      },
      {
        icon: <DriveFolderUploadOutlined className="thin" />,
        to: "/processing/uploaded-sizers",
        label: "Uploaded Sizers",
      },
    ];

    return [
      {
        items: baseItems,
      },
    ];
  }, [isLendmarq, isAdmin]);

  const bottomLinks = useMemo(
    () => [
      {
        items: [
          ...(isLendmarq && isAdmin
            ? [
                {
                  icon: <EmailOutlined className="thin" />,
                  to: "/email/templates",
                  label: "Email Templates",
                },
              ]
            : []),
          {
            icon: <PersonOutlined />,
            to: "/profile",
            label: "Profile",
          },
          {
            icon: <ScienceOutlined />,
            to: "/changelog",
            label: "Changelog",
          },
          {
            type: "logout",
            label: "Log Out",
            onClick: handleLogoutUser,
            icon: <LogoutOutlined sx={{ transform: "rotate(180deg)" }} />,
          },
        ],
      },
    ],
    [handleLogoutUser, isAdmin],
  );

  const { getLoggedInUser, getAllUsers } = useUsersHook();
  const { getLoanFileDocGroupsAndTypes } = useFetchedSelectionsRequestsHook();
  useEffect(() => {
    if (isNil(user)) {
      getLoggedInUser({
        onSuccessFn: (data) => dispatch(userActionSet(data)),
        onErrorFn: (err) => console.error("GetLoggedInUser error:", err),
      });
    }
    if (isNil(users)) {
      getAllUsers({
        onSuccessFn: (data) => dispatch(usersActionSet(data)),
        onErrorFn: (err) => console.error("GetAllUsers error:", err),
      });
    }
    if (isNil(fileDocGroupAndTypes)) {
      getLoanFileDocGroupsAndTypes({});
    }
  }, []);

  return (
    <DashboardLayout topLinks={topLinks} bottomLinks={bottomLinks} open={open}>
      {children}
    </DashboardLayout>
  );
};

export default AuthenticatedDashboard;
